<template>
  <div>
    <div
      class="mt-3 bg-white font-weight-bold p-2 border-r-2 d-flex justify-content-between align-items-center"
      style="border-radius: 8px 0 0 8px; border: solid 1px #eaeaea; cursor: pointer"
      @click="isExpanded = !isExpanded"
    >
      <div>
        <i class="far fa-envelope ml-2 mr-2" :id="'emailicon' + vendorRequest.id"></i>
        <b-tooltip :target="'emailicon' + vendorRequest.id">
          <div v-for="contact in vendorRequest.vendor.vendorContacts" :key="contact.id">
            {{ contact.contact }}
          </div>
        </b-tooltip>
        <span>{{ vendorRequest.vendor.name }}</span>
        <button :id="'remarks' + vendorRequest.id" class="badgebutton small ml-2" @click="openEditRemark"><i class="fa-comment" :class="{ 'fa': vendorRequest.remarks, 'far': !vendorRequest.remarks}"></i> Remarks</button>

        <b-tooltip :target="'remarks' + vendorRequest.id" triggers="hover" :disabled="!vendorRequest.remarks">
          <div style="white-space: pre-wrap">
          {{vendorRequest.remarks}}
          </div>
        </b-tooltip>

      </div>

      <div v-if="!vendorRequest.vendor.isExternallyOrderedVendor">
        <span v-if="noPartsAvailable" class="badge badge-danger">No parts available</span>
        <div v-else>
          <button
            class="badgebutton danger active mr-2"
            @click="openSetAllPartsAsNotAvailableModal"
            v-if="vendorRequest.canMarkAsNoPartsAvailable"
          >
            <i class="far fa-times-circle"></i>
            No parts available
          </button>

          <button class="badgebutton active" @click="previewEmail" v-if="vendorRequest.vendor.vendorContacts.filter(_ => _.contactType === 'email').length">
            <i class="far fa-envelope"></i>
            Send request
          </button>

          <span v-b-tooltip="vendorRequest.availableShippers.length ? '' : 'No shipper for available parts selected'">
            <button
              v-if="vendorRequest.availableShippers.length < 2"
              class="ml-2 badgebutton active"
              @click="previewLogisticsEmail(vendorRequest.availableShippers[0].shipperId)"
              :disabled="!vendorRequest.availableShippers.length"
            >
              <i class="far fa-envelope"></i>
              Send logistics email
            </button>

            <button
              v-if="vendorRequest.availableShippers.length >= 2"
              :id="'sendMail' + vendorRequest.id"
              class="ml-2 badgebutton active"
              :disabled="!vendorRequest.availableShippers.length"
            >
              <i class="far fa-envelope"></i>
              Send logistics email
            </button>

            <b-popover :target="'sendMail' + vendorRequest.id" triggers="click blur">
              <button
                class="btn btn-block btn-info"
                @click="previewLogisticsEmail(shipper.shipperId)"
                v-for="shipper in vendorRequest.availableShippers"
                :key="shipper.shipperId"
              >
                {{ shipper.shipperName }} ({{ shipper.numberOfParts }}
                <span v-if="shipper.numberOfParts == 1">part)</span>
                <span v-else>parts)</span>
              </button>
            </b-popover>
          </span>
          <div></div>
        </div>
      </div>

      <div v-else>
        <span v-if="vendorRequest.vendor.isExternallyOrderedVendor" class="badge badge-info">EXTERNALLY ORDERED</span>
      </div>

      <b-modal
        :ref="'email'"
        :id="'email'"
        centered
        body-class="modal-bodyas-wo"
        hide-footer
        size="wide"
        title="E-Mail to vendor"
        :no-enforce-focus="true"
        :no-close-on-backdrop="true"
      >
        <VendorRequestEmail ref="emailText" :type="'request'" :vendor-request-id="vendorRequest.id"></VendorRequestEmail>

        <hr class="mt-3" />
        <div class="mt-3 d-flex justify-content-end">
          <button class="mr-2 btn btn-secondary btn-sm" @click="closeEmailModal">
            <i class="far fa-times"></i>
            Cancel
          </button>
          <button class="btn btn-primary btn-sm" @click="sendEmail">
            <i class="far fa-envelope"></i>
            Send E-Mail
          </button>
        </div>
      </b-modal>

      <b-modal
        :ref="'emailLogistics'"
        :id="'emailLogistics'"
        centered
        body-class="modal-bodyas-wo"
        hide-footer
        size="wide"
        title="Logistics E-Mail"
        :no-enforce-focus="true"
        :no-close-on-backdrop="true"
      >
        <div class="text-centered" v-if="logisticsEmailLoading">
          <loading-spinner></loading-spinner>
        </div>
        <div v-else v-html="emailPreview"></div>

        <hr class="mt-3" />
        <div class="mt-3 d-flex justify-content-end">
          <button class="mr-2 btn btn-secondary btn-sm" @click="closeLogisticsEmailModal">
            <i class="far fa-times"></i>
            Cancel
          </button>
          <button class="btn btn-primary btn-sm" @click="sendLogisticsEmail(currentShipperId)">
            <i class="far fa-envelope"></i>
            Send E-Mail
          </button>
        </div>
      </b-modal>
    </div>

    <grid
      class="ml-4"
      :data-items="vendorRequest.parts"
      :columns="vendorRequestColumns"
      style="font-size: 0.9rem"
      :filterable="false"
      :cell-render="'cellTemplate'"
      v-show="!noPartsAvailable || isExpanded"
    >
      <template v-slot:cellTemplate="{ props }">
        <td v-if="props.field === 'pn'">
          {{ props.dataItem['part']['pn'] }}
          <br />
          <small>{{ props.dataItem['part']['altPn'] }}</small>
        </td>

        <td v-else-if="props.field === 'ipc'">
          {{ props.dataItem['part']['ipc'] }}
          <br />
          <small>{{ props.dataItem['part']['altIpc'] }}</small>
        </td>

        <td v-else-if="props.field === 'quantityRequested'">
          {{ props.dataItem[props.field] }}
        </td>

        <td v-else-if="props.field === 'quantityAvailable'">
          {{ props.dataItem[props.field] }}
        </td>

        <td v-else-if="props.field === 'location'">
          {{ props.dataItem[props.field] }}
        </td>

        <td v-else-if="props.field === 'dateAvailable'">
          <div v-if="props.dataItem[props.field]">
            <span class="badge badge-secondary">AVA</span>
            {{ props.dataItem[props.field] | formatDate }}
          </div>

          <div v-if="props.dataItem['estimatedTimeOfArrival']">
            <span class="badge badge-primary">ETA</span>
            {{ props.dataItem['estimatedTimeOfArrival'] | formatDate }}
          </div>

          <div v-if="props.dataItem['actualTimeOfArrival']">
            <span class="badge badge-success">ATA</span>
            {{ props.dataItem['actualTimeOfArrival'] | formatDate }}
          </div>
        </td>

        <td v-else-if="props.field === 'pricePerUnit'">{{ props.dataItem['currency'] }} {{ props.dataItem[props.field] }}</td>

        <td v-else-if="props.field === 'requestStatus' || props.field === 'orderStatus' || props.field === 'shipmentStatus'">
          <div v-if="props.dataItem[props.field]" :class="props.dataItem[props.field].cssClass">
            {{ props.dataItem[props.field].name }}
          </div>
          <div v-else class="badge badge-secondary">N/A</div>
          <div v-if="props.field === 'orderStatus' && props.dataItem['amosOrderNo']">
            <i class="far fa-link"></i>
            &nbsp;
            <a :href="'amos://order.' + props.dataItem['amosOrderNo']">{{ props.dataItem['amosOrderNo'] }}</a>
          </div>
          <div v-if="props.field === 'requestStatus' && props.dataItem['dateRequested']">
            <i class="far fa-envelope mr-1"></i>
            {{ props.dataItem['dateRequested'] | formatDate }}
          </div>

          <div v-if="props.field === 'shipmentStatus' && props.dataItem['shipmentRequestedAt']">
            <i class="far fa-envelope mr-1"></i>
            {{ props.dataItem['shipmentRequestedAt'] | formatDate }}
          </div>
        </td>

        <td v-else-if="props.field === 'action'" style="text-align: right">
          <div class="text-nowrap">
            <button class="badgebutton active" @click="editVendorRequestPart(props.dataItem['id'])">
              <i class="far fa-pencil text-white" />
            </button>
            <button class="badgebutton active danger ml-2" @click="openRemovePartModal(props.dataItem['id'])">
              <i class="far fa-trash text-white" />
            </button>
          </div>
        </td>

        <td v-else>
          {{ props.dataItem[props.field] }}
        </td>
      </template>
    </grid>

    <b-modal id="removePartModal" ref="removePartModal" hide-footer hide-header body-class="modal-body-wo" centered>
      <modal-window :main-width="12">
        <div class="d-block">
          <h1>
            <i class="far fa-trash"></i>
            Remove Part
          </h1>
          <p>
            Do you really want to remove this part from the vendor request? If this part is the last part of the request, the request will
            be removed entirely.
          </p>
        </div>
        <hr class="mt-3" />
        <div class="mt-3 d-flex justify-content-end">
          <button class="mr-2 btn btn-secondary btn-sm" @click="cancelRemovePart">Cancel</button>
          <button class="btn btn-danger btn-sm" @click="removePart">Remove part</button>
        </div>
      </modal-window>
    </b-modal>

    <b-modal id="setAllPartsAsNotAvailableModal" ref="setAllPartsAsNotAvailableModal" hide-footer hide-header body-class="modal-body-wo" centered>
      <modal-window :main-width="12">
        <div class="d-block">
          <h1>
            <i class="far fa-trash"></i>
            Set all parts as not available
          </h1>
          <p>
            Do you really want to set all parts of this vendor as not available?
          </p>
        </div>
        <hr class="mt-3" />
        <div class="mt-3 d-flex justify-content-end">
          <button class="mr-2 btn btn-secondary btn-sm" @click="closeSetAllPartsAsNotAvailableModal">Cancel</button>
          <button class="btn btn-danger btn-sm" @click="setAllPartsAsNotAvailable">Set parts as not available</button>
        </div>
      </modal-window>
    </b-modal>

    <b-modal id="editRemark" ref="editRemark" hide-footer hide-header body-class="modal-body-wo" centered>
      <modal-window :main-width="12" :paddings="'p-3'">
        <div class="d-block">
          <h1>
            <i class="far fa-comment"></i>
            Edit Vendor Request Remark
          </h1>
          <textarea v-model="vendorRequest.remarks" rows="8" class="form-control"></textarea>
        </div>
        <hr class="mt-3" />
        <div class="mt-3 d-flex justify-content-end">
          <button class="mr-2 btn btn-secondary btn-sm" @click="closeEditRemark">Cancel</button>
          <button class="btn btn-success btn-sm" @click="saveRemark">Save remarks</button>
        </div>
      </modal-window>
    </b-modal>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import {
    RequestStatusEnum,
    SendLogisticsEmailDto,
    SendVendorRequestDto,
    VendorRequestDto,
    VendorRequestEditRemarksDto
  } from "@/apiclient/apiclient_generated";
  import { Emit, Prop } from 'vue-property-decorator';
  import TechnicalOccurrenceFlightsSelector from '@/components/SpecialOccurrence/Data/Flights/TechnicalOccurrenceFlightsSelector.vue';
  import { GridColumnProps } from '@progress/kendo-vue-grid';
  import AddVendor from '@/components/SpecialOccurrence/MaterialRequest/AddVendor.vue';
  import VendorRequestEmail from '@/components/SpecialOccurrence/MaterialRequest/VendorRequestEmail.vue';
  import { materialRequestClient } from '@/apiclient/opsboardapi';
  import { GlobalHelper } from '@/helpers/global-helper';
  import ModalWindow from '@/components/ModalWindow.vue';
  import LoadingSpinner from '@/components/Misc/LoadingSpinner.vue';

  @Component({
    components: { LoadingSpinner, ModalWindow, VendorRequestEmail, AddVendor, TechnicalOccurrenceFlightsSelector },
  })
  export default class VendorRequest extends Vue {
    @Prop() vendorRequest: VendorRequestDto;

    materialRequestId: string;
    isExpanded = false;

    emailPreview = '';
    logisticsEmailLoading = false;
    currentShipperId = '';

    created() {
      this.materialRequestId = this.$router.currentRoute.params.occId;
    }

    get isExternallyOrdered(): boolean {
      return this.vendorRequest.vendor.isExternallyOrderedVendor;
    }

    get noPartsAvailable(): boolean {
      const parts = this.vendorRequest.parts;
      const notAvailable = this.vendorRequest.parts.filter((p) => p.requestStatus.requestStatusEnum == RequestStatusEnum.NotAvailable);
      return parts.length == notAvailable.length;
    }

    get vendorRequestColumns(): GridColumnProps[] {
      return [
        { field: 'pn', title: 'PN', filterable: false, width: 100 },
        { field: 'ipc', title: 'IPC', filterable: false, width: 100 },
        { field: 'quantityRequested', title: '# Req.', filterable: false, width: 60 },
        { field: 'quantityAvailable', title: '# Avail.', filterable: false, width: 60 },
        { field: 'quantityOrdered', title: '# Ordered', filterable: false, width: 75 },
        { field: 'location', title: 'Location', filterable: false, width: 130 },
        { field: 'dateAvailable', title: 'Time', filterable: false, width: 150 },
        { field: 'pricePerUnit', title: 'Price', filterable: false, width: 120 },
        { field: 'requestStatus', title: 'Request Status', filterable: false },
        { field: 'orderStatus', title: 'Order Status', filterable: false },
        { field: 'shipmentStatus', title: 'Shipment Status', filterable: false },
        { field: 'action', title: ' ', filterable: false, width: 190 },
      ];
    }

    previewEmail() {
      const modal: any = this.$refs['email'];
      modal.show();
    }

    closeEmailModal() {
      const modal: any = this.$refs['email'];
      modal.hide();
    }

    async previewLogisticsEmail(shipperId: string) {
      const modal: any = this.$refs['emailLogistics'];
      modal.show();

      this.logisticsEmailLoading = true;
      this.currentShipperId = shipperId;
      this.emailPreview = await materialRequestClient.getLogisticsEmailById(this.vendorRequest.id, shipperId);
      this.logisticsEmailLoading = false;
    }

    closeLogisticsEmailModal() {
      const modal: any = this.$refs['emailLogistics'];
      modal.hide();
    }

    @Emit('reload')
    async sendEmail() {
      try {
        const dto = new SendVendorRequestDto();
        dto.vendorRequestId = this.vendorRequest.id;
        await materialRequestClient.sendVendorRequest(dto);
        GlobalHelper.NotifySuccess('E-Mail was sent');
        this.closeEmailModal();
      } catch (e) {
        GlobalHelper.NotifyError('Error sending E-Mail');
      }
    }

    @Emit('reload')
    async sendLogisticsEmail(currentShipperId: string) {
      try {
        const dto = new SendLogisticsEmailDto();
        dto.vendorRequestId = this.vendorRequest.id;
        dto.shipperId = currentShipperId;
        await materialRequestClient.sendLogisticsEmail(dto);
        GlobalHelper.NotifySuccess('E-Mail was sent');
        this.closeLogisticsEmailModal();
        this.emailPreview = '';
        this.currentShipperId = '';
      } catch (e) {
        GlobalHelper.NotifyError('Error sending E-Mail');
      }
    }

    editVendorRequestPart(vendorRequestPartId: string) {
      this.$router.push({
        name: 'MaterialRequestVendorRequestPartEdit',
        params: { materialRequestId: this.materialRequestId, vendorRequestPartId: vendorRequestPartId },
      });
    }

    editVendorRequestPartOrder(vendorRequestPartId: string) {
      this.$router.push({
        name: 'MaterialRequestVendorRequestPartOrderEdit',
        params: { materialRequestId: this.materialRequestId, vendorRequestPartId: vendorRequestPartId },
      });
    }

    partToBeRemovedId = '';
    openRemovePartModal(id: string) {
      this.partToBeRemovedId = id;
      const modal: any = this.$refs['removePartModal'];
      modal.show();
    }

    cancelRemovePart() {
      const modal: any = this.$refs['removePartModal'];
      modal.hide();
      this.partToBeRemovedId = '';
    }

    @Emit('reload')
    async removePart() {
      if (this.partToBeRemovedId) {
        await materialRequestClient.deleteVendorRequestPart(this.partToBeRemovedId);
        this.partToBeRemovedId = '';
        const modal: any = this.$refs['removePartModal'];
        modal.hide();
      }
    }

    openSetAllPartsAsNotAvailableModal() {
      const modal: any = this.$refs['setAllPartsAsNotAvailableModal'];
      modal.show();
    }

    closeSetAllPartsAsNotAvailableModal() {
      const modal: any = this.$refs['setAllPartsAsNotAvailableModal'];
      modal.hide();
    }

    @Emit('reload')
    async setAllPartsAsNotAvailable() {
      try {
        await materialRequestClient.markAllPartsAsNotAvailable(this.vendorRequest.id);
        GlobalHelper.NotifySuccess('Parts set as not available');
        this.closeSetAllPartsAsNotAvailableModal();
      } catch (e) {
        GlobalHelper.NotifyError('Error settings parts as not available');
      }
    }

    openEditRemark() {
      const modal: any = this.$refs['editRemark'];
      modal.show();
    }

    @Emit('reload')
    closeEditRemark() {
      const modal: any = this.$refs['editRemark'];
      modal.hide();
    }

    @Emit('reload')
    async saveRemark() {
        const dto = VendorRequestEditRemarksDto.fromJS({});
        dto.id = this.vendorRequest.id;
        dto.remarks = this.vendorRequest.remarks;

        await materialRequestClient.editVendorRequestRemarks(dto);
        this.closeEditRemark();
      }
  }
</script>

<style lang="scss" scoped>
  .k-grid td {
    border-width: 0 !important;
  }
</style>
